import React, {useMemo, useState} from "react";
import {FaEye, FaTrash} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import Button from "../../../../components/button/button.jsx";
import AttachShow from "../../../../components/attach/attach-show.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const STATUS_CLASS = {
    done: "bg-[#33804E]",
    delayed: "bg-[#EF5744]",
    inProgress: "bg-[#287499]",
    todo: "bg-[#6A4D9F]",
    notDone: "bg-[#e3714f]",
};

const StatusBadge = ({activity}) => {
    if (!activity?.status) return null;

    return (
        <span className={`whitespace-nowrap rounded-[2px] px-1 py-0.5 font-bold text-white ${STATUS_CLASS[activity.status] ?? "bg-gray-500"}`}>
            {activity.statusText}
        </span>
    );
};

const ScheduleActivitiesGrid = ({schedule, showDispatchAction, onDispatchActivity, openDispatch}) => {
    const columnDefs = useMemo(() => {
        const columns = [
            {
                field: "statusText",
                headerName: "Status",
                    width: 135,
                cellRenderer: ({data}) => <StatusBadge activity={data}/>,
            },
            {
                field: "startText",
                headerName: "Programada (duração)",
                width: 175,
                valueGetter: ({data}) => `${data?.startText ?? ""}${data?.duration ? ` (${data.duration})` : ""}`,
            },
            {
                field: "dispatchList",
                headerName: "Realizada (duração)",
                minWidth: 210,
                flex: 1,
                autoHeight: true,
                cellRenderer: ({data}) => (
                    <div className="py-1">
                        {(data?.dispatchList ?? []).map(dispatch => (
                            <div key={dispatch.dispatchId ?? `${dispatch.numberCheckbook}-${dispatch.dispatchStart}`}>
                                <button
                                    type="button"
                                    className="a-link cursor-pointer"
                                    onClick={event => openDispatch(event, dispatch.dispatchId)}
                                >
                                    {dispatch.numberCheckbook}
                                </button>
                                {" - "}
                                {dispatch.dispatchStart ?? ""}
                                {dispatch.dispatchDuration ? ` (${dispatch.dispatchDuration})` : ""}
                            </div>
                        ))}
                    </div>
                ),
            },
            {field: "reason", headerName: "Natureza", minWidth: 160, flex: 1, wrapText: true, autoHeight: true},
            {field: "priority", headerName: "Prioridade", width: 110},
            {field: "placeDescr", headerName: "Endereço", minWidth: 190, flex: 1, wrapText: true, autoHeight: true},
            {field: "description", headerName: "Descrição", minWidth: 150, flex: 1, wrapText: true, autoHeight: true},
        ];

        if (showDispatchAction) {
            columns.splice(3, 0, {
                field: "allowNewDispatch",
                headerName: "Despacho",
                width: 125,
                sortable: false,
                cellRenderer: ({data: activity}) => activity?.allowNewDispatch === true ? (
                    <Button
                        type="button"
                        target="primary"
                        title="Despachar atividade programada"
                        className="mt-0.5 w-full h-6!"
                        onClick={() => onDispatchActivity({schedule, activity})}
                    >
                        Despachar
                    </Button>
                ) : null,
            });
        }

        return columns;
    }, [onDispatchActivity, openDispatch, schedule, showDispatchAction]);

    return (
        <AgGridReact
            domLayout="autoHeight"
            rowData={schedule.activities ?? []}
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
            animateRows={false}
            suppressCellFocus={true}
            gridOptions={{localeText: aggridUtils.localization()}}
            theme={aggridUtils.generalTheme()}
            overlayNoRowsTemplate="Nenhuma atividade."
        />
    );
};

const GarrisonScheduleList = ({schedules = [], showDetails = false, onRemove, onDispatchActivity}) => {
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const navigate = navigateHook();
    const showDispatchAction = typeof onDispatchActivity === "function";

    const openDispatch = (event, dispatchId) => {
        if (dispatchId == null) return;

        event.preventDefault();
        event.stopPropagation();
        navigate(`${API_DESPACHO_WEB}/dispatch/form/${dispatchId}`, true);
    };

    if (!schedules.length) return null;

    return (
        <>
            <div className="space-y-5">
                {schedules.map(schedule => (
                    <div key={schedule.id}>
                        <div className="mb-2 flex items-center justify-between gap-3 text-left">
                            <h4 className="text-[14px] font-semibold text-[#676a6c]">{schedule.name}</h4>
                            {schedule.removable && onRemove && (
                                <Button
                                    className="h-2.5 w-7.5 !p-0"
                                    target="danger"
                                    title="Remover atividade programada"
                                    onClick={() => onRemove(schedule.id)}
                                >
                                    <FaTrash size={12}/>
                                </Button>
                            )}
                        </div>

                        {showDetails && (
                            <div className="mb-2 space-y-1 text-sm text-[#676a6c]">
                                {schedule.description !== undefined && (
                                    <div><strong>Descrição: </strong>{schedule.description}</div>
                                )}

                                {(schedule.attachment ?? []).length > 0 && (
                                    <div>
                                        <strong>Anexos:</strong>
                                        {(schedule.attachment ?? []).map(attachment => {
                                            const attachId = attachment.reference ?? attachment.referenceId ?? attachment.attachId;

                                            return (
                                                <div key={attachment.id ?? attachId} className="ml-2 flex items-center gap-2">
                                                    {attachId && (
                                                        <button
                                                            type="button"
                                                            className="cursor-pointer text-[#525252]"
                                                            title="Visualizar anexo"
                                                            onClick={() => setSelectedAttachment({attachId, name: attachment.name})}
                                                        >
                                                            <FaEye />
                                                        </button>
                                                    )}
                                                    <span>{attachment.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <ScheduleActivitiesGrid
                            schedule={schedule}
                            showDispatchAction={showDispatchAction}
                            onDispatchActivity={onDispatchActivity}
                            openDispatch={openDispatch}
                        />
                    </div>
                ))}
            </div>

            <AttachShow
                open={!!selectedAttachment}
                setOpen={open => !open && setSelectedAttachment(null)}
                attachId={selectedAttachment?.attachId}
                showRemove={false}
                modalTitle={selectedAttachment?.name ?? "Anexo"}
            />
        </>
    );
};

export default GarrisonScheduleList;
